import React, { useCallback, useEffect, useState, FC } from 'react';
import Slider from 'react-slick';
import { ComponentConsumerProps, ComponentRendering, Placeholder, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { CarouselDirectionEnum } from '../../enums/carousel.enums';

import { windowService } from '../../utils/window.utils';
import { findCtaType } from '../../utils/popUpCtaType.utils';
import CustomArrow from '../@shared/carouselArrow';
import CarouselDots from '../@shared/carouselDots';
import Heading from '../@shared/heading';
import Picture from '../@shared/picture';
import CarouselSlide from './components/carouselSlide';
import { TSpecialOffersProps } from './types/cardCarousel.types';
import { SpecialOffersModel, SpecialOffersSlideModel } from './models/cardCarousel.model';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AppendDots from './components/appendDots';
import useMediaQuery from '../../hooks/useMediaQuery';
import '../../styles/@shared/special-offers.scss';
import './index.scss';

const CardCarousel: FC<ComponentConsumerProps & TSpecialOffersProps> = (props) => {
  const isEE = props.sitecoreContext?.pageEditing;
  const model = new SpecialOffersModel(props);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [screenWidth, setScreenWidth] = useState(1680);
  const containerPaddingLeft = 32;
  const largeImageWidth = isDesktop ? 664 : 300;
  const smallImageWidth = isDesktop ? 392 : 300;
  const [activeCardIndex, updateActiveIndex] = useState(0);

  const totalElementsWidth =
    model.items?.reduce((sumWidths: number, curr: SpecialOffersSlideModel) => {
      if (curr?.type?.value === 'offerCardWide') {
        sumWidths += largeImageWidth;
      } else {
        sumWidths += smallImageWidth;
      }

      return sumWidths;
    }, 0) + containerPaddingLeft;
  const shouldInstanceCarousel = totalElementsWidth > screenWidth;

  const getCarouselDots = useCallback(() => <CarouselDots />, []);
  const showMinValueSlider = isDesktop ? 5 : 1;
  const itemsLength = model?.items?.length;

  const settings = {
    infinite: !isEE && shouldInstanceCarousel,
    dots: true,
    arrows: !isEE && shouldInstanceCarousel,
    variableWidth: true,
    speed: 500,
    slidesToScroll: 1,
    nextArrow: isDesktop ? <CustomArrow direction={CarouselDirectionEnum.NEXT} /> : null,
    prevArrow: isDesktop ? <CustomArrow direction={CarouselDirectionEnum.PREVIOUS} /> : null,
    customPaging: getCarouselDots,
    // eslint-disable-next-line react/display-name
    appendDots: (dots) => AppendDots({ activeCardIndex, dots, showMinValueSlider, itemsLength }),
    afterChange: (index): void => {
      updateActiveIndex(index);
    },
  };

  const iconimage = 'arrow-chevron-right';

  useEffect(() => {
    setScreenWidth(Math.min(windowService()?.innerWidth, 1680));
  }, []);

  const popUpCtaType = findCtaType(props.rendering);
  const secondaryCTAClassName = popUpCtaType === 'primary-cta' ? 'special-offers__ml-24' : '';
  const popUpClassName = isEE ? ' generic-popupEE-width' : '';

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return model.items.length > 0 ? (
    <div className="special-offers-wrapper card-carousel">
      {/* eslint-disable-next-line */}
      <div className="special-offers">
        <div className="special-offers__background-wrapper">
          <Picture
            editorialImageProps={{
              desktopImage: model?.desktopBgImage,
              mobileImage: model?.mobileBgImage,
              desktopTransformation: 'Size1900',
              mobileTransformation: 'Size1000',
              cssClassImageEl: 'special-offers__background-image',
            }}
          />
          <Heading
            title={model.title}
            eyebrow={model.eyebrow}
            subtitle={model.subtitle}
            link={model.link}
            editable={true}
            extraClassNameLink={secondaryCTAClassName}
            customTextTitle
            tag={model.headingTag}
            isGenericPopup={!!props.rendering?.placeholders?.['jss-generic-popup']?.length}
          >
            <div className={'special-offers__pop-up' + popUpClassName}>
              <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
            </div>
          </Heading>
        </div>
        <Slider {...settings}>
          {model.items.map((item) => (
            <div key={item?.cardId} className="special-offers__slide">
              <CarouselSlide model={item} isEE={isEE} iconimage={iconimage} defaultCardImage={model.defaultCardImage} />
            </div>
          ))}
        </Slider>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default withSitecoreContext()(CardCarousel);
